import { useState, useEffect } from 'react';
import '../styles/App.css';
import CharacterCreation from './CharacterCreation.js';
import Intro from './Intro.js';
import Play2 from './Play2.js';
import cdoAI from '../cdo/cdoAI.js';
import cdoBeing from '../cdo/cdoBeing.js';
import { mediaSource } from '../shared/mediaSource';
import cdoContract from '../cdo/cdoContract.js';
import cdoCurrencyList from '../cdo/cdoCurrencyList.js';
import cdoMasterType from '../cdo/cdoMasterType.js';
import cdoPrimarySkill from '../cdo/cdoPrimarySkill.js';
import cdoPrimarySkillList from '../cdo/cdoPrimarySkillList.js';
import cdoSpellList from '../cdo/cdoSpellList.js';
import cdoSpellUsage from '../cdo/cdoSpellUsage.js';

const AppGenerator = require('./AppGenerator.js').default;

const ADENREL_CHARACTER_COOKIE = 'Adenrel';
const ADENREL_CHARACTER_CREATION_COMPLETE_COOKIE = 'AdenrelCharacterCreationComplete';
const STAT_POINT_TOTAL = 75;
const PRIMARY_SKILL_POINT_TOTAL = 18;
const CURRENT_AGE_ID = 503;
const CURRENT_YEAR = 1000;
const CURRENT_SEASON_ID = 513;
const CURRENT_DAY = 1;

const STAT_FIELDS = ['strength', 'agility', 'constitution', 'intellect', 'wisdom', 'charisma'];

const callCdo = (fn, ...args) =>
  new Promise((resolve, reject) => {
    fn(...args, (err, response) => {
      if (err || response?.error) {
        reject(response?.error ?? err);
        return;
      }

      resolve(response);
    });
  });

const readCookie = (name) => {
  const cookie = document.cookie
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(`${name}=`));

  return cookie ? decodeURIComponent(cookie.slice(name.length + 1)) : '';
};

const writeCookie = (name, value) => {
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=31536000`;
};

const deleteCookie = (name) => {
  document.cookie = `${name}=; path=/; max-age=0`;
};

export const clearCharacterCookies = () => {
  deleteCookie(ADENREL_CHARACTER_COOKIE);
  deleteCookie(ADENREL_CHARACTER_CREATION_COMPLETE_COOKIE);
};

const isUuid = (value) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(String(value ?? '').trim());

const isCharacterCreationCompleteForCookie = (characterId) => {
  const normalizedCharacterId = String(characterId ?? '').trim();
  const completedCharacterId = readCookie(ADENREL_CHARACTER_CREATION_COMPLETE_COOKIE);
  return (
    isUuid(normalizedCharacterId) &&
    String(completedCharacterId ?? '').trim().toLowerCase() === normalizedCharacterId.toLowerCase()
  );
};

const normalizePrimarySkillLists = (rows) =>
  (Array.isArray(rows) ? rows : [])
    .map((row) => ({
      idPrimarySkillList: row.idPrimarySkillList,
      name: row.name ?? '',
      stat: row.stat ?? '',
      description: row.description ?? '',
      gmNotes: row.gmNotes ?? '',
    }))
    .sort((left, right) => Number(left.idPrimarySkillList) - Number(right.idPrimarySkillList));

const getCurrencyIdByName = (currencyLists = [], name) => {
  const match = (Array.isArray(currencyLists) ? currencyLists : []).find(
    (currency) => String(currency.name ?? '').trim().toLowerCase() === name
  );

  return match?.idCurrency ? String(match.idCurrency) : '';
};

const buildPrimarySkills = (primarySkillLists, rows = []) =>
  primarySkillLists.map((skillList) => {
    const existing = rows.find((row) => Number(row.idPrimarySkillList) === Number(skillList.idPrimarySkillList));
    return {
      idPrimarySkill: existing?.idPrimarySkill ?? '',
      idPrimarySkillList: skillList.idPrimarySkillList,
      name: skillList.name,
      stat: skillList.stat,
      description: skillList.description,
      gmNotes: skillList.gmNotes,
      level: Number.isFinite(Number(existing?.level)) ? Number(existing.level) : 0,
    };
  });

const createEmptyPortraitState = () => ({
  src: '',
  revisedPrompt: '',
  isGenerating: false,
  isSaving: false,
  isSaved: false,
  hasCheckedSavedImage: false,
  error: '',
});

const createEmptyCharacter = (primarySkillLists = [], currencyLists = []) => {
  const startingCurrencies = ['gold', 'silver', 'copper']
    .map((name) => ({ idCurrency: getCurrencyIdByName(currencyLists, name), quantity: 10 }))
    .filter((currency) => currency.idCurrency);

  return {
    idEntity: '',
    name: '',
    title: '',
    description: '',
    entityType: 'Being',
    level: 1,
    beingType: '',
    strength: 0,
    agility: 0,
    constitution: 0,
    intellect: 0,
    wisdom: 0,
    charisma: 0,
    gender: '',
    age: '',
    createDateAge: CURRENT_AGE_ID,
    createDateYear: '',
    createDateSeason: CURRENT_SEASON_ID,
    createDateDay: CURRENT_DAY,
    heraldry: '',
    defaultForm: '',
    multipleForms: false,
    personality: '',
    objectivesShortTerm: '',
    objectivesLongTerm: '',
    maxStamina: 100,
    currentStamina: 100,
    hunger: 0,
    thirst: 0,
    intoxication: 0,
    lastVitalsAdvancedMinute: '',
    ownedItems: [],
    contracts: [],
    currencies: startingCurrencies,
    spells: [],
    forms: [],
    relationships: [],
    itemSuggestions: [],
    primarySkills: buildPrimarySkills(primarySkillLists),
  };
};

const toCharacterSheetValue = (being, primarySkillLists = [], primarySkillRows = [], currencyLists = []) => {
  const source = being?.response && typeof being.response === 'object' ? being.response : being ?? {};
  const base = createEmptyCharacter(primarySkillLists, currencyLists);
  const sourceAge = Number(source.age);
  const sourceCreateYear = Number(source.createDateYear);
  const age = Number.isFinite(sourceAge) && sourceAge > 0
    ? Math.round(sourceAge)
    : Number.isFinite(sourceCreateYear) && sourceCreateYear > 0
      ? Math.max(1, CURRENT_YEAR - sourceCreateYear)
      : '';

  return {
    ...base,
    idEntity: source.idEntity ?? '',
    name: source.name ?? source.entityName ?? '',
    title: source.title ?? source.entityTitle ?? '',
    description: source.description ?? source.entityDesc ?? '',
    level: 1,
    beingType: source.beingType ?? '',
    gender: source.gender ?? '',
    age,
    createDateAge: source.createDateAge ?? CURRENT_AGE_ID,
    createDateYear: source.createDateYear ?? (age ? CURRENT_YEAR - age : ''),
    createDateSeason: source.createDateSeason ?? CURRENT_SEASON_ID,
    createDateDay: source.createDateDay ?? CURRENT_DAY,
    heraldry: source.heraldry ?? '',
    defaultForm: source.beingType ?? '',
    multipleForms: false,
    personality: '',
    objectivesShortTerm: '',
    objectivesLongTerm: '',
    currentHp: source.currentHp ?? source.currentHealth ?? source.health,
    maxHp: source.maxHp ?? source.maxHealth,
    currentPp: source.currentPp ?? source.currentMana ?? source.mana,
    maxPp: source.maxPp ?? source.maxMana,
    maxStamina: source.maxStamina ?? source.max_stamina ?? 100,
    currentStamina: source.currentStamina ?? source.current_stamina ?? source.maxStamina ?? source.max_stamina ?? 100,
    hunger: source.hunger ?? 0,
    thirst: source.thirst ?? 0,
    intoxication: source.intoxication ?? 0,
    lastVitalsAdvancedMinute: source.lastVitalsAdvancedMinute ?? source.last_vitals_advanced_minute ?? '',
    ownedItems: Array.isArray(source.ownedItems) ? source.ownedItems : [],
    contracts: Array.isArray(source.contracts) ? source.contracts : [],
    currencies: Array.isArray(source.currencies ?? source.currency) ? source.currencies ?? source.currency : [],
    spells: Array.isArray(source.spells ?? source.spell) ? source.spells ?? source.spell : [],
    forms: [],
    relationships: [],
    itemSuggestions: [],
    primarySkills: buildPrimarySkills(primarySkillLists, primarySkillRows),
    ...STAT_FIELDS.reduce(
      (stats, field) => ({
        ...stats,
        [field]: Number.isFinite(Number(source[field])) ? Number(source[field]) : 0,
      }),
      {}
    ),
  };
};

export const mergeCompletedPlayCharacter = (nextCharacter, currentCharacter, referenceData) => ({
  ...toCharacterSheetValue(nextCharacter, referenceData.primarySkillLists,
    Array.isArray(nextCharacter.primarySkills) ? nextCharacter.primarySkills : currentCharacter.primarySkills,
    referenceData.currencyLists),
  contracts: Array.isArray(nextCharacter.contracts) ? nextCharacter.contracts : currentCharacter.contracts || [],
});

const formatLoadError = (error) => {
  if (!error) {
    return 'Unable to load character data.';
  }

  if (typeof error === 'string') {
    return error;
  }

  return error.message || error.sqlMessage || JSON.stringify(error);
};

const numberOrZero = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const getCharacterAge = (character) => {
  const explicitAge = Number(character?.age);
  if (Number.isFinite(explicitAge) && explicitAge > 0) {
    return Math.round(explicitAge);
  }

  const createYear = Number(character?.createDateYear);
  if (Number.isFinite(createYear) && createYear > 0) {
    return Math.max(1, CURRENT_YEAR - createYear);
  }

  return 0;
};

const getStatTotal = (character) =>
  STAT_FIELDS.reduce((total, field) => total + numberOrZero(character?.[field]), 0);

const getPrimarySkillTotal = (character) =>
  (Array.isArray(character?.primarySkills) ? character.primarySkills : []).reduce(
    (total, skill) => total + numberOrZero(skill.level),
    0
  );

const canSaveCharacter = (character) =>
  Boolean(String(character?.name ?? '').trim()) &&
  Boolean(String(character?.beingType ?? '').trim()) &&
  Boolean(String(character?.description ?? '').trim()) &&
  getStatTotal(character) === STAT_POINT_TOTAL &&
  getPrimarySkillTotal(character) === PRIMARY_SKILL_POINT_TOTAL;

const buildCharacterSavePayload = (character) => {
  const name = String(character?.name ?? '').trim();
  const beingType = character?.beingType ?? '';
  const age = getCharacterAge(character);
  const rawMaxStamina = Number(character?.maxStamina);
  const maxStamina = Number.isFinite(rawMaxStamina) && rawMaxStamina > 1 ? rawMaxStamina : 100;
  const rawCurrentStamina = Number(character?.currentStamina);
  const currentStamina =
    character?.currentStamina !== '' && Number.isFinite(rawCurrentStamina) && rawCurrentStamina > 0
      ? rawCurrentStamina
      : maxStamina;

  return {
    idEntity: character?.idEntity ?? '',
    entityName: name,
    entityTitle: name,
    entityDesc: character?.description ?? '',
    name,
    title: name,
    description: character?.description ?? '',
    entityType: 1,
    level: 1,
    age: age || '',
    createDateAge: age ? CURRENT_AGE_ID : null,
    createDateYear: age ? CURRENT_YEAR - age : null,
    createDateSeason: age ? CURRENT_SEASON_ID : null,
    createDateDay: age ? CURRENT_DAY : null,
    beingType,
    defaultForm: beingType,
    multipleForms: false,
    personality: '',
    objectivesShortTerm: '',
    objectivesLongTerm: '',
    maxStamina,
    currentStamina,
    hunger: Number.isFinite(Number(character?.hunger)) ? Number(character.hunger) : 0,
    thirst: Number.isFinite(Number(character?.thirst)) ? Number(character.thirst) : 0,
    intoxication: Number.isFinite(Number(character?.intoxication)) ? Number(character.intoxication) : 0,
    forms: [],
    relationships: [],
    itemSuggestions: [],
    ...STAT_FIELDS.reduce(
      (stats, field) => ({
        ...stats,
        [field]: numberOrZero(character?.[field]),
      }),
      {}
    ),
    alignment: null,
    gender: character?.gender ?? '',
    heraldry: character?.heraldry ?? '',
    currencies: Array.isArray(character?.currencies) ? character.currencies : [],
    spells: Array.isArray(character?.spells) ? character.spells : [],
    primarySkills: Array.isArray(character?.primarySkills)
      ? character.primarySkills.map((skill) => ({
          idPrimarySkill: skill.idPrimarySkill ?? '',
          idPrimarySkillList: skill.idPrimarySkillList,
          level: numberOrZero(skill.level),
        }))
      : [],
  };
};

function App({ selectedCharacter = null, onExit = null, awaitCharacterWorld = null, generateCharacterDescription = null, GameComponent = Play2 }) {
  const [activeView, setActiveView] = useState(() => window.location.pathname === '/admin' ? 'edit' : 'intro');
  const [character, setCharacter] = useState(() => createEmptyCharacter());
  const [characterPortrait, setCharacterPortrait] = useState(() => createEmptyPortraitState());
  const [isCharacterLoading, setIsCharacterLoading] = useState(false);
  const [isCharacterSaving, setIsCharacterSaving] = useState(false);
  const [isWaitingForCharacterWorld, setIsWaitingForCharacterWorld] = useState(false);
  const [isGeneratingCharacterDescription, setIsGeneratingCharacterDescription] = useState(false);
  const [characterError, setCharacterError] = useState('');
  const [characterStatus, setCharacterStatus] = useState('');
  const [characterReferenceData, setCharacterReferenceData] = useState({
    masterTypes: [],
    spellLists: [],
    spellUsages: [],
    currencyLists: [],
    primarySkillLists: [],
  });
  const [, setPreloadedSettlement] = useState(null);

  const handlePlay = async () => {
    const characterId = selectedCharacter ? (selectedCharacter.beingCreated ? selectedCharacter.characterId : '') : readCookie(ADENREL_CHARACTER_COOKIE);
    const shouldSkipCharacterCreation = selectedCharacter ? selectedCharacter.status === 'ready' : isCharacterCreationCompleteForCookie(characterId);

    setActiveView(shouldSkipCharacterCreation ? 'play-session' : 'play');
    setIsCharacterLoading(true);
    setCharacterError('');
    setCharacterStatus('');
    setPreloadedSettlement(null);

    if (shouldSkipCharacterCreation) {
      // Play owns the ready character/session load. Its identity is known before
      // mounting; an asynchronously populated sheet must not restart the scene.
      setCharacter(current => ({ ...current, idEntity: characterId }));
      setIsCharacterLoading(false);
      return;
    }

    try {
      const [masterTypes, spellLists, spellUsages, currencyLists, primarySkillListRows, primarySkillRows] =
        selectedCharacter?.creationCatalog ? await selectedCharacter.creationCatalog.then(catalog => [
          catalog.masterTypes, catalog.spellLists, catalog.spellUsages, catalog.currencyLists, catalog.primarySkillLists, [],
        ]) : await Promise.all([
          callCdo(cdoMasterType.selectAll),
          callCdo(cdoSpellList.selectAll),
          callCdo(cdoSpellUsage.selectAll),
          callCdo(cdoCurrencyList.selectAll),
          callCdo(cdoPrimarySkillList.selectAll),
          callCdo(cdoPrimarySkill.selectAll),
        ]);

      const primarySkillLists = normalizePrimarySkillLists(primarySkillListRows);
      const hasCharacterCookie = isUuid(characterId);
      let loadedCharacter = null;
      let loadedContracts = [];

      if (hasCharacterCookie) {
        const [characterResponse, contractResponse] = await Promise.all([
          callCdo(cdoBeing.select, characterId),
          callCdo(cdoContract.selectByEntity, characterId).catch(() => []),
        ]);
        loadedCharacter = characterResponse;
        loadedContracts = Array.isArray(contractResponse) ? contractResponse : [];
      }

      setCharacterReferenceData({
        masterTypes: Array.isArray(masterTypes) ? masterTypes : [],
        spellLists: Array.isArray(spellLists) ? spellLists : [],
        spellUsages: Array.isArray(spellUsages) ? spellUsages : [],
        currencyLists: Array.isArray(currencyLists) ? currencyLists : [],
        primarySkillLists,
      });

      const existingPrimarySkillRows = hasCharacterCookie
        ? (Array.isArray(primarySkillRows) ? primarySkillRows : []).filter(
            (row) => String(row.idEntity).toLowerCase() === String(characterId).toLowerCase()
          )
        : [];

      setCharacter(
        loadedCharacter
          ? {
              ...toCharacterSheetValue(loadedCharacter, primarySkillLists, existingPrimarySkillRows, currencyLists),
              contracts: loadedContracts,
            }
          : createEmptyCharacter(primarySkillLists, currencyLists)
      );

      if (shouldSkipCharacterCreation && !loadedCharacter) {
        setActiveView('play');
      }

      if (loadedCharacter) {
        setCharacterStatus('');

        try {
          const portrait = await callCdo(cdoBeing.selectPortraitImage, characterId);
          setCharacterPortrait(
            mediaSource(portrait)
              ? {
                  src: mediaSource(portrait),
                  revisedPrompt: portrait.revisedPrompt || '',
                  isGenerating: false,
                  isSaving: false,
                  isSaved: true,
                  hasCheckedSavedImage: true,
                  error: '',
                }
              : { ...createEmptyPortraitState(), hasCheckedSavedImage: true }
          );
        } catch (portraitError) {
          setCharacterPortrait({
            ...createEmptyPortraitState(),
            error: formatLoadError(portraitError),
          });
        }
      } else {
        setCharacterStatus('');
        setCharacterPortrait(createEmptyPortraitState());
      }
    } catch (error) {
      if (shouldSkipCharacterCreation) {
        setActiveView('play');
      }
      setCharacter(createEmptyCharacter(characterReferenceData.primarySkillLists, characterReferenceData.currencyLists));
      setCharacterPortrait(createEmptyPortraitState());
      setCharacterError(formatLoadError(error));
    } finally {
      setIsCharacterLoading(false);
    }
  };

  const handleNewCharacter = async () => {
    clearCharacterCookies();
    setCharacter(createEmptyCharacter());
    setCharacterPortrait(createEmptyPortraitState());
    setCharacterError('');
    setCharacterStatus('');
    setPreloadedSettlement(null);
    await handlePlay();
  };

  useEffect(() => {
    if (selectedCharacter) handlePlay();
    // A new character selection remounts this component; do not reload on drafts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCharacter?.characterId]);

  const handleSaveCharacter = async () => {
    const payload = buildCharacterSavePayload(character);
    const statTotal = getStatTotal(payload);
    const primarySkillTotal = payload.primarySkills.reduce((total, skill) => total + numberOrZero(skill.level), 0);

    if (!payload.entityName) {
      setCharacterError('Save requires a character name.');
      return;
    }

    if (!payload.beingType) {
      setCharacterError('Save requires a form.');
      return;
    }

    if (!String(payload.description ?? '').trim()) {
      setCharacterError('Save requires a description.');
      return;
    }

    if (statTotal !== STAT_POINT_TOTAL) {
      setCharacterError('Save requires all 75 stat points to be allotted.');
      return;
    }

    if (primarySkillTotal !== PRIMARY_SKILL_POINT_TOTAL) {
      setCharacterError('Save requires all 18 primary ability points to be allotted.');
      return;
    }

    setIsCharacterSaving(true);
    setCharacterError('');
    setCharacterStatus('Saving character...');

    try {
      const saveAction = payload.idEntity ? cdoBeing.update : cdoBeing.create;
      await awaitCharacterWorld?.();
      const saveResult = await callCdo(saveAction, payload);
      const savedId = saveResult?.idEntity ?? payload.idEntity;

      if (!savedId) {
        throw new Error('Save completed without returning a character id.');
      }

      writeCookie(ADENREL_CHARACTER_COOKIE, savedId);

      const [loadedCharacter, primarySkillRows, loadedContracts] = await Promise.all([
        callCdo(cdoBeing.select, savedId),
        callCdo(cdoPrimarySkill.selectAll),
        callCdo(cdoContract.selectByEntity, savedId).catch(() => []),
      ]);
      const characterPrimarySkills = (Array.isArray(primarySkillRows) ? primarySkillRows : []).filter(
        (row) => String(row.idEntity).toLowerCase() === String(savedId).toLowerCase()
      );

      setCharacter({
        ...toCharacterSheetValue(
          loadedCharacter,
          characterReferenceData.primarySkillLists,
          characterPrimarySkills,
          characterReferenceData.currencyLists
        ),
        contracts: Array.isArray(loadedContracts) ? loadedContracts : [],
      });
      setCharacterStatus('Saved character.');
    } catch (error) {
      setCharacterError(formatLoadError(error));
      setCharacterStatus('');
    } finally {
      setIsCharacterSaving(false);
    }
  };

  const handleSaveCharacterDraft = async (draftCharacter = character) => {
    const payload = {
      ...buildCharacterSavePayload({
        ...draftCharacter,
        alignment: null,
      }),
      alignment: null,
    };
    const statTotal = getStatTotal(payload);
    const primarySkillTotal = payload.primarySkills.reduce((total, skill) => total + numberOrZero(skill.level), 0);

    if (!payload.entityName) {
      throw new Error('Save requires a character name.');
    }

    if (!payload.beingType) {
      throw new Error('Save requires a form.');
    }

    if (!payload.gender) {
      throw new Error('Save requires a gender.');
    }

    if (!payload.age || Number(payload.age) <= 0) {
      throw new Error('Save requires a starting age.');
    }

    if (!String(payload.description ?? '').trim()) {
      throw new Error('Save requires a description.');
    }

    if (statTotal !== STAT_POINT_TOTAL) {
      throw new Error('Save requires all 75 stat points to be allotted.');
    }

    if (primarySkillTotal !== PRIMARY_SKILL_POINT_TOTAL) {
      throw new Error('Save requires all 18 primary ability points to be allotted.');
    }

    setIsCharacterSaving(true);
    setCharacterError('');
    setCharacterStatus('Saving character draft...');

    try {
      const saveAction = payload.idEntity ? cdoBeing.update : cdoBeing.create;
      if (awaitCharacterWorld) {
        setIsWaitingForCharacterWorld(true);
        try { await awaitCharacterWorld(); }
        finally { setIsWaitingForCharacterWorld(false); }
      }
      const saveResult = await callCdo(saveAction, payload);
      const savedId = saveResult?.idEntity ?? payload.idEntity;

      if (!savedId) {
        throw new Error('Save completed without returning a character id.');
      }

      writeCookie(ADENREL_CHARACTER_COOKIE, savedId);
      deleteCookie(ADENREL_CHARACTER_CREATION_COMPLETE_COOKIE);

      const [loadedCharacter, primarySkillRows, loadedContracts] = await Promise.all([
        callCdo(cdoBeing.select, savedId),
        callCdo(cdoPrimarySkill.selectAll),
        callCdo(cdoContract.selectByEntity, savedId).catch(() => []),
      ]);
      const characterPrimarySkills = (Array.isArray(primarySkillRows) ? primarySkillRows : []).filter(
        (row) => String(row.idEntity).toLowerCase() === String(savedId).toLowerCase()
      );
      const nextCharacter = {
        ...toCharacterSheetValue(
          loadedCharacter,
          characterReferenceData.primarySkillLists,
          characterPrimarySkills,
          characterReferenceData.currencyLists
        ),
        alignment: null,
        contracts: Array.isArray(loadedContracts) ? loadedContracts : [],
      };

      setCharacter(nextCharacter);
      setCharacterStatus('Saved character draft.');
      return nextCharacter;
    } catch (error) {
      const formattedError = formatLoadError(error);
      setCharacterError(formattedError);
      setCharacterStatus('');
      throw new Error(formattedError);
    } finally {
      setIsCharacterSaving(false);
    }
  };

  const buildCharacterDescriptionRewritePayload = () => ({
    name: character.name || '',
    form: character.beingType || '',
    age: getCharacterAge(character) || '',
    heraldry: character.heraldry || '',
    description: character.description || '',
    stats: STAT_FIELDS.reduce(
      (stats, field) => ({
        ...stats,
        [field]: numberOrZero(character?.[field]),
      }),
      {}
    ),
    primarySkills: (Array.isArray(character?.primarySkills) ? character.primarySkills : []).map((skill) => ({
      name: skill.name,
      level: numberOrZero(skill.level),
      stat: skill.stat,
      description: skill.description,
    })),
  });

  const buildCharacterPortraitPrompt = ({ normalizedDescription, imageGuidance } = {}) => {
    const statSummary = STAT_FIELDS.map((field) => `${field}: ${numberOrZero(character?.[field])}`).join(', ');
    const abilitySummary = (Array.isArray(character?.primarySkills) ? character.primarySkills : [])
      .filter((skill) => numberOrZero(skill.level) > 0)
      .map((skill) => `${skill.name}: ${numberOrZero(skill.level)}`)
      .join(', ');
    const portraitDescription =
      String(normalizedDescription ?? '').trim() ||
      character.description ||
      'A first-level adventurer at the start of their journey.';

    return `Create a portrait image of this Adenrel player character.

Name: ${character.name || 'Unnamed'}
Form: ${character.beingType || 'Human'}
Age: ${getCharacterAge(character) || 'Unspecified'}
Heraldry: ${character.heraldry || 'None'}
Description: ${portraitDescription}
Stats: ${statSummary}
Primary abilities: ${abilitySummary || 'Unassigned'}
Image guidance: ${String(imageGuidance ?? '').trim() || 'Keep the portrait grounded for a first-level character.'}

Visual requirements:
- Show the character clearly as the main subject.
- Compose for a square 1:1 character-sheet image.
- Make the character look like a new first-level adventurer, not a deity, ruler, master hero, or legendary champion.
- Use heraldry only as visual inspiration for colors, symbols, badges, embroidery, shield marks, or clothing motifs.
- Use portrait composition suitable for a character sheet.
- Do not include text, labels, UI, logos, or watermarks.`;
  };

  const handleGenerateCharacterDescription = async () => {
    setIsGeneratingCharacterDescription(true);
    setCharacterError('');
    setCharacterStatus('Generating first-level character description...');

    try {
      const payload = buildCharacterDescriptionRewritePayload();
      const rewriteResponse = generateCharacterDescription
        ? await generateCharacterDescription(payload)
        : await callCdo(cdoAI.rewriteCharacterDescription, payload);
      const normalizedDescription = String(rewriteResponse?.normalizedDescription ?? '').trim();

      if (!normalizedDescription) {
        throw new Error('Description generation returned no description.');
      }

      setCharacter((current) => ({
        ...current,
        description: normalizedDescription,
      }));
      setCharacterStatus(rewriteResponse?.changed ? 'Generated adjusted first-level description.' : 'Generated description.');
    } catch (error) {
      setCharacterError(formatLoadError(error));
      setCharacterStatus('');
    } finally {
      setIsGeneratingCharacterDescription(false);
    }
  };

  const saveCharacterPortrait = async (portrait) => {
    if (!character.idEntity || !portrait?.src) {
      return false;
    }

    const base64Marker = ';base64,';
    const markerIndex = portrait.src.indexOf(base64Marker);
    if (markerIndex < 0) {
      setCharacterPortrait((current) => ({
        ...current,
        error: 'Generated image data is not in a savable format.',
      }));
      return false;
    }

    const mediaType = portrait.src.slice(5, markerIndex) || 'image/png';
    const imageBase64 = portrait.src.slice(markerIndex + base64Marker.length);

    setCharacterPortrait((current) => ({
      ...current,
      isSaving: true,
      error: '',
    }));

    try {
      await callCdo(cdoBeing.savePortraitImage, {
        idEntity: character.idEntity,
        name: character.name,
        title: character.name,
        mediaType,
        imageBase64,
        revisedPrompt: portrait.revisedPrompt || '',
      });

      setCharacterPortrait((current) => ({
        ...current,
        isSaving: false,
        isSaved: true,
        error: '',
      }));
      return true;
    } catch (error) {
      setCharacterPortrait((current) => ({
        ...current,
        isSaving: false,
        error: formatLoadError(error),
      }));
      return false;
    }
  };

  const handleGenerateCharacterPortrait = async () => {
    if (!character.idEntity) {
      setCharacterPortrait((current) => ({
        ...current,
        error: 'Save the character before generating a portrait.',
      }));
      return;
    }

    const shouldAutoSaveGeneratedImage = characterPortrait.hasCheckedSavedImage && !characterPortrait.src;
    setCharacterPortrait((current) => ({
      ...current,
      isGenerating: true,
      isSaved: false,
      error: '',
    }));

    try {
      setCharacterStatus('Preparing character description for portrait...');
      const rewriteResponse = await callCdo(cdoAI.rewriteCharacterDescription, buildCharacterDescriptionRewritePayload());
      const normalizedDescription = String(rewriteResponse?.normalizedDescription ?? character.description ?? '').trim();
      const imageGuidance = String(rewriteResponse?.imageGuidance ?? '').trim();

      if (normalizedDescription && normalizedDescription !== String(character.description ?? '').trim()) {
        setCharacter((current) => ({
          ...current,
          description: normalizedDescription,
        }));
      }

      setCharacterStatus('Generating character portrait...');
      const response = await callCdo(cdoAI.generateImage, {
        beingId: character.idEntity,
        aogYear: 0,
        size: '1024x1024',
        prompt: buildCharacterPortraitPrompt({
          normalizedDescription,
          imageGuidance,
        }),
      });

      const generatedPortrait = {
        src: response?.imageBase64 ? `data:${response?.mimeType || 'image/png'};base64,${response.imageBase64}` : '',
        revisedPrompt: response?.revisedPrompt || rewriteResponse?.changeSummary || '',
        isGenerating: false,
        isSaving: false,
        isSaved: false,
        hasCheckedSavedImage: true,
        error: response?.imageBase64 ? '' : 'Image generation returned no image data.',
      };
      setCharacterPortrait(generatedPortrait);
      const saved = shouldAutoSaveGeneratedImage && generatedPortrait.src
        ? await saveCharacterPortrait(generatedPortrait)
        : false;
      setCharacterStatus(saved
        ? 'Generated and saved character portrait.'
        : rewriteResponse?.changed ? 'Generated portrait with adjusted first-level description.' : 'Generated portrait.');
    } catch (error) {
      setCharacterPortrait((current) => ({
        ...current,
        isGenerating: false,
        error: formatLoadError(error),
      }));
      setCharacterStatus('');
    }
  };

  const handleSaveCharacterPortrait = async () => {
    await saveCharacterPortrait(characterPortrait);
  };

  const handleContinueCharacter = () => {
    setCharacterStatus('');
    setActiveView('play-session');
  };

  const refreshCharacterFromServer = async (characterId = character.idEntity) => {
    const normalizedCharacterId = String(characterId ?? '').trim();
    if (!isUuid(normalizedCharacterId)) {
      return null;
    }

    const [loadedCharacter, primarySkillRows, loadedContracts] = await Promise.all([
      callCdo(cdoBeing.select, normalizedCharacterId),
      callCdo(cdoPrimarySkill.selectAll),
      callCdo(cdoContract.selectByEntity, normalizedCharacterId).catch(() => []),
    ]);
    const characterPrimarySkills = (Array.isArray(primarySkillRows) ? primarySkillRows : []).filter(
      (row) => String(row.idEntity).toLowerCase() === normalizedCharacterId.toLowerCase()
    );
    const nextCharacter = {
      ...toCharacterSheetValue(
        loadedCharacter,
        characterReferenceData.primarySkillLists,
        characterPrimarySkills,
        characterReferenceData.currencyLists
      ),
      contracts: Array.isArray(loadedContracts) ? loadedContracts : [],
    };
    setCharacter(nextCharacter);
    return nextCharacter;
  };

  const handleCharacterCreationComplete = async ({ character: finalizedCharacter, portrait, preloadedSettlement: preload } = {}) => {
    const characterId = finalizedCharacter?.idEntity ?? character.idEntity;
    setPreloadedSettlement(preload ?? null);
    setCharacterError('');
    setCharacterStatus('');

    if (isUuid(characterId)) {
      writeCookie(ADENREL_CHARACTER_COOKIE, characterId);
      writeCookie(ADENREL_CHARACTER_CREATION_COMPLETE_COOKIE, characterId);
    }

    try {
      if (characterId) {
        await refreshCharacterFromServer(characterId);
      } else if (finalizedCharacter) {
        setCharacter(
          toCharacterSheetValue(
            finalizedCharacter,
            characterReferenceData.primarySkillLists,
            [],
            characterReferenceData.currencyLists
          )
        );
      }
    } catch (error) {
      setCharacterError(formatLoadError(error));
      if (finalizedCharacter) {
        setCharacter(
          toCharacterSheetValue(
            finalizedCharacter,
            characterReferenceData.primarySkillLists,
            [],
            characterReferenceData.currencyLists
          )
        );
      }
    }

    if (portrait?.src) {
      setCharacterPortrait({
        src: portrait.src,
        revisedPrompt: portrait.revisedPrompt || '',
        isGenerating: false,
        isSaving: false,
        isSaved: true,
        hasCheckedSavedImage: true,
        error: '',
      });
    } else if (characterId) {
      try {
        const savedPortrait = await callCdo(cdoBeing.selectPortraitImage, characterId);
        setCharacterPortrait(
          mediaSource(savedPortrait)
            ? {
                src: mediaSource(savedPortrait),
                revisedPrompt: savedPortrait.revisedPrompt || '',
                isGenerating: false,
                isSaving: false,
                isSaved: true,
                hasCheckedSavedImage: true,
                error: '',
              }
            : { ...createEmptyPortraitState(), hasCheckedSavedImage: true }
        );
      } catch (portraitError) {
        setCharacterPortrait({
          ...createEmptyPortraitState(),
          error: formatLoadError(portraitError),
        });
      }
    }

    if (onExit) { await onExit(); return; }
    setActiveView('play-session');
  };

  const handlePlayCharacterRefresh = (nextCharacter) => {
    if (!nextCharacter?.idEntity) {
      return;
    }

    setCharacterError('');
    setCharacter(currentCharacter => mergeCompletedPlayCharacter(nextCharacter, currentCharacter, characterReferenceData));
  };

  const handlePlayBootstrap = ({ player, references }) => {
    const catalog = { ...references, primarySkillLists: normalizePrimarySkillLists(references.primarySkillLists) };
    setCharacterReferenceData(catalog);
    setCharacter(current => mergeCompletedPlayCharacter(player, current.idEntity === player.idEntity ? current : {}, catalog));
  };

  const characterSheetProps = {
    value: character,
    onChange: setCharacter,
    onSave: handleSaveCharacter,
    canSaveCharacter: canSaveCharacter(character),
    isLoading: isCharacterLoading,
    isSaving: isCharacterSaving,
    errorMessage: characterError,
    statusMessage: characterStatus,
    masterTypes: characterReferenceData.masterTypes,
    spellListOptions: characterReferenceData.spellLists.map((spellList) => ({
      value: spellList.idSpellList ?? spellList.name,
      label: spellList.name,
      name: spellList.name,
      description: spellList.description ?? '',
      gmNotes: spellList.gmNotes ?? '',
    })),
    currencyOptions: characterReferenceData.currencyLists.map((currency) => ({
      value: String(currency.idCurrency),
      label: currency.name,
    })),
    startingCurrencyId: getCurrencyIdByName(characterReferenceData.currencyLists, 'silver'),
    primarySkillOptions: characterReferenceData.primarySkillLists,
    portrait: characterPortrait,
    canGeneratePortrait: Boolean(character.idEntity),
    canSavePortrait: Boolean(character.idEntity && characterPortrait.src && !characterPortrait.isSaved),
    isGeneratingDescription: isGeneratingCharacterDescription,
    onGenerateDescription: handleGenerateCharacterDescription,
    onGeneratePortrait: handleGenerateCharacterPortrait,
    onSavePortrait: handleSaveCharacterPortrait,
    onContinue: handleContinueCharacter,
  };

  const characterCreationProps = {
    value: character,
    onChange: setCharacter,
    onSaveDraft: handleSaveCharacterDraft,
    onComplete: handleCharacterCreationComplete,
    isLoading: isCharacterLoading,
    isSaving: isCharacterSaving,
    isWaitingForWorld: isWaitingForCharacterWorld,
    errorMessage: characterError,
    statusMessage: characterStatus,
    masterTypes: characterReferenceData.masterTypes,
    primarySkillOptions: characterReferenceData.primarySkillLists,
    isGeneratingDescription: isGeneratingCharacterDescription,
    onGenerateDescription: handleGenerateCharacterDescription,
  };

  if (activeView === 'edit') {
    return <AppGenerator />;
  }

  if (activeView === 'play') {
    return <CharacterCreation {...characterCreationProps} onExit={onExit} />;
  }

  if (activeView === 'play-session') {
    return (
      <GameComponent
        playerId={character.idEntity}
        onBootstrap={handlePlayBootstrap}
        characterSheetProps={characterSheetProps}
        onCharacterRefresh={handlePlayCharacterRefresh}
        onExitGame={onExit || (() => setActiveView('intro'))}
      />
    );
  }

  return (
    <Intro
      onPlay={handlePlay}
      onNewCharacter={handleNewCharacter}
    />
  );
}

export default App;
